(ns frontend.view.ai-summary-library.view
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.entities.ai-summary-configuration.options :as options]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.ui.ai-summary-configuration.editor :as configuration-editor]
   [frontend.view.ai-summary-library.event :as event]
   [frontend.view.ai-summary-library.model :as model]
   [frontend.view.ai-summary-library.sub :as sub]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.view.modals.events :as modals]
   [frontend.view.not-found :as not-found]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.breadcrumbs.view :as breadcrumbs]
   [ui.components.button.view :as button]
   [ui.editors.labelled-input.view :as labelled-input]))

(defn- breadcrumbs-view
  []
  [breadcrumbs/container
   [breadcrumbs/item
    {:active? true}
    (loc/tr :ai-summary-library/title)]])

(defn- access-copy-key
  [access-level]
  (case access-level
    :ai-summary-configuration.access-level/restricted
    :ai-summary-library/access-restricted

    :ai-summary-configuration.access-level/all-users
    :ai-summary-library/access-all-users

    :ai-summary-library/access-private))

(defn- owner-label
  [owner-id]
  (if (= owner-id @session/account-id)
    (loc/tr :ai-summary-library/owner-you)
    (or (some-> owner-id session/get-entity entity/display-string)
        (loc/tr :ai-summary-library/owner-unknown))))

(defn- configuration-card
  [configuration selected?]
  (let [{:keys [id configuration-name configuration-description owner-id
                configuration-access-level default-pipeline-ids]}
        configuration]
    [:button
     {:class (str
              "w-full rounded border p-3 text-left transition-colors "
              (if selected?
                "border-primary-regular-border bg-primary-lighter"
                "border-base-light-border-paler bg-base-light hover:bg-base-regular"))
      :on-click (fn [_]
                  (rf/dispatch [::event/select-configuration id]))}
     [:div
      {:class "flex items-start justify-between gap-3"}
      [:div
       {:class "min-w-0 flex-1"}
       [:div
        {:class "truncate font-semibold text-base-light-text"}
        configuration-name]
       (when-not (empty? configuration-description)
         [:div
          {:class "mt-1 line-clamp-2 text-sm text-base-light-text-paler"}
          configuration-description])]
      (when (seq default-pipeline-ids)
        [:span
         {:class "rounded bg-success-lighter px-2 py-0.5 text-xs text-success-dark-text"}
         (loc/tr :ai-summary-library/default-badge)])]
     [:div
      {:class "mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-base-light-text-paler"}
      [:span
       (loc/tr :ai-summary-library/owner-prefix)
       " "
       (owner-label owner-id)]
      [:span (loc/tr (access-copy-key configuration-access-level))]]]))

(defn- library-list
  [state]
  (let [configurations (model/filtered-configurations state)
        selected-id (:selected-configuration-id state)]
    [:div
     {:class "flex min-h-0 flex-col gap-3"}
     [labelled-input/view
      {:label (loc/tr :ai-summary-library/search-label)
       :data-type :data.type/text-simple
       :value (:query state)
       :placeholder (loc/tr :ai-summary-library/search-placeholder)
       :on-change (fn [query]
                    (rf/dispatch [::event/set-query query]))}]
     [:div
      {:class "flex min-h-0 flex-col gap-2 overflow-y-auto pr-1"}
      (cond
        (= :loading (:load-state state))
        [:div
         {:class "flex items-center justify-center py-12 text-base-light-text-paler"}
         [:i.fal.fa-spinner.fa-pulse.fa-2x]]

        (= :failed (:load-state state))
        [:div
         {:class "rounded border border-danger-light-border bg-danger-lighter p-4 text-sm text-danger-light-text"}
         (loc/tr :ai-summary-library/load-failed)]

        (empty? (:configurations state))
        [:div
         {:class "rounded border border-dashed border-base-light-border-paler p-5 text-center"}
         [:div
          {:class "font-semibold text-base-light-text"}
          (loc/tr :ai-summary-library/empty-title)]
         [:div
          {:class "mt-1 text-sm text-base-light-text-paler"}
          (loc/tr :ai-summary-library/empty-description)]]

        (empty? configurations)
        [:div
         {:class "py-8 text-center text-sm text-base-light-text-paler"}
         (loc/tr :ai-summary-library/no-search-results)]

        :else
        (for [configuration configurations]
          ^{:key (:id configuration)}
          [configuration-card
           configuration
           (= selected-id (:id configuration))]))]]))

(defn- reference-options
  [entities]
  (->> entities
       (map (fn [reference]
              {:value (:db/id reference)
               :label (entity/display-string reference)}))
       (sort-by :label)
       vec))

(defn- access-options
  []
  [{:value options/access-level-private
    :label (loc/tr :ai-summary-library/access-private)}
   {:value options/access-level-restricted
    :label (loc/tr :ai-summary-library/access-restricted)}
   {:value options/access-level-all-users
    :label (loc/tr :ai-summary-library/access-all-users)}])

(defn- sharing-options
  []
  (reference-options
   (concat (remove (fn [account]
                     (= (:db/id account) @session/account-id))
                   (session/tenant-users))
           @session/teams)))

(defn- pipeline-options
  []
  (reference-options
   (remove :pipeline/inactive? @session/oppo-pipelines)))

(defn- management-editor
  [{:keys [draft disabled?]}]
  (let [pipeline-options (pipeline-options)
        applicable-ids (:applicable-pipeline-ids draft)
        default-options (if (seq applicable-ids)
                          (filterv (fn [{:keys [value]}]
                                     (contains? applicable-ids value))
                                   pipeline-options)
                          pipeline-options)]
    [:div
     {:class "flex flex-col gap-4 rounded border border-base-light-border-paler p-4"}
     [:div
      {:class "font-semibold text-base-light-text"}
      (loc/tr :ai-summary-library/access-and-pipelines)]
     [labelled-input/view
      {:label (loc/tr :ai-summary-library/access-level)
       :editor-type :editor.type/select
       :disabled? disabled?
       :options (access-options)
       :value (:configuration-access-level draft)
       :on-change (fn [value _option]
                    (rf/dispatch [::event/update-draft
                                  :configuration-access-level
                                  value]))}]
     (when (= options/access-level-restricted
              (:configuration-access-level draft))
       [labelled-input/view
        {:label (loc/tr :ai-summary-library/shared-with)
         :editor-type :editor.type/multi-select
         :disabled? disabled?
         :options (sharing-options)
         :value (:use-access-ids draft)
         :on-change (fn [values _selected-options]
                      (rf/dispatch [::event/update-draft
                                    :use-access-ids
                                    (set values)]))}])
     [labelled-input/view
      {:label (loc/tr :ai-summary-library/applicable-pipelines)
       :editor-type :editor.type/multi-select
       :disabled? disabled?
       :options pipeline-options
       :value applicable-ids
       :placeholder (loc/tr :ai-summary-library/all-deal-pipelines)
       :on-change (fn [values _selected-options]
                    (rf/dispatch [::event/update-draft
                                  :applicable-pipeline-ids
                                  (set values)]))}]
     [:div
      {:class "text-xs text-base-light-text-paler"}
      (loc/tr :ai-summary-library/applicable-pipelines-hint)]
     [labelled-input/view
      {:label (loc/tr :ai-summary-library/default-pipelines)
       :editor-type :editor.type/multi-select
       :disabled? disabled?
       :options default-options
       :value (:default-pipeline-ids draft)
       :on-change (fn [values _selected-options]
                    (rf/dispatch [::event/update-draft
                                  :default-pipeline-ids
                                  (set values)]))}]]))

(defn- delete-confirmation!
  [configuration]
  (modals/open-modal!
   confirmation/modal-key
   {:title (loc/tr :ai-summary-library/delete-title)
    :body (loc/tr :ai-summary-library/delete-description
                  [(:configuration-name configuration)])
    :confirm-text (loc/tr :ai-summary-library/delete-action)
    :checkbox-text (loc/tr :ai-summary-library/delete-confirmation)
    :confirm-icon nil
    :on-confirm (fn []
                  (rf/dispatch [::event/delete-configuration
                                (:id configuration)]))}))

(defn- save-status
  [save-state]
  (when (contains? #{:saved :failed :upload-failed :invalid-template} save-state)
    [:div
     {:class (if (= :saved save-state)
               "text-sm text-success-regular"
               "text-sm text-danger-light-text")}
     (loc/tr
      (case save-state
        :saved :ai-summary-library/save-succeeded
        :upload-failed :deal-one-pager/document-upload-failed
        :invalid-template :deal-one-pager/template-file-invalid
        :ai-summary-library/save-failed))]))

(defn- editor-actions
  [state]
  (let [{:keys [mode save-state configuration-detail]} state
        owner-edit? (model/owner-edit? state)
        copy-mode? (model/copy-mode? state)
        saving? (contains? #{:saving :deleting} save-state)
        selected (model/selected-configuration state)]
    [:div
     {:class "flex flex-wrap items-center justify-between gap-3 border-t border-base-light-border-paler pt-4"}
     [:div
      {:class "flex flex-wrap gap-2"}
      (when (and owner-edit? (not (session/is-read-only-user?)))
        [button/sm-danger-solid
         {:disabled? saving?
          :on-click (fn [_]
                      (delete-confirmation! selected))}
         [:i.fal.fa-trash]
         (loc/tr :ai-summary-library/delete-action)])
      (when (and (= :selected mode)
                 configuration-detail
                 (not (session/is-read-only-user?)))
        [button/sm-secondary-hollow
         {:disabled? saving?
          :on-click (fn [_]
                      (rf/dispatch [::event/prepare-copy]))}
         [:i.fal.fa-copy]
         (loc/tr (if owner-edit?
                   :ai-summary-library/duplicate-action
                   :deal-one-pager/save-as-new))])]
     [:div
      {:class "flex flex-wrap gap-2"}
      (when (contains? #{:create :copy} mode)
        [button/sm-secondary-hollow
         {:disabled? saving?
          :on-click (fn [_]
                      (rf/dispatch [::event/cancel-edit]))}
         (loc/tr :cancel)])
      (when (and (not (session/is-read-only-user?))
                 (not (and (= :selected mode)
                           (not owner-edit?))))
        [button/sm-primary-solid
         {:disabled? (or saving?
                         (not (model/save-enabled? state)))
          :on-click (fn [_]
                      (rf/dispatch [::event/save]))}
         (if saving?
           (loc/tr :ai-summary-library/saving)
           (loc/tr
            (cond
              (= :create mode) :ai-summary-library/create-action
              copy-mode? :deal-one-pager/save-as-new
              :else :deal-one-pager/update-configuration)))])]]))

(defn- configuration-editor-view
  [state]
  (let [{:keys [mode draft configuration-detail uploading-field save-state]} state
        owner-edit? (model/owner-edit? state)
        content-disabled? (session/is-read-only-user?)
        management-visible? (or (= :create mode) owner-edit?)]
    [:div
     {:class "flex flex-col gap-5"}
     [:div
      [:div
       {:class "text-xl font-bold text-base-light-text"}
       (loc/tr
        (case mode
          :create :ai-summary-library/create-title
          :copy :ai-summary-library/copy-title
          :ai-summary-library/edit-title))]
      [:div
       {:class "mt-1 text-sm text-base-light-text-paler"}
       (cond
         (= :copy mode)
         (loc/tr :ai-summary-library/private-copy-hint)

         (and (= :selected mode) (not owner-edit?))
         (loc/tr :ai-summary-library/shared-copy-hint)

         :else
         (loc/tr :ai-summary-library/editor-hint))]]
     (when (= :copy mode)
       [:div
        {:class "rounded border border-primary-light-border bg-primary-lighter p-3 text-sm text-base-light-text"}
        (loc/tr :deal-one-pager/copy-name-required)])
     [configuration-editor/view
      {:draft draft
       :disabled? content-disabled?
       :uploading-field uploading-field
       :on-change (fn [field value]
                    (rf/dispatch [::event/update-draft field value]))
       :on-toggle-source (fn [source selected?]
                           (rf/dispatch [::event/toggle-source source selected?]))
       :on-upload (fn [field file]
                    (rf/dispatch [::event/upload-document field file]))
       :on-clear (fn [field]
                   (rf/dispatch [::event/clear-document field]))}]
     (when management-visible?
       [management-editor
        {:draft draft
         :disabled? content-disabled?}])
     (when (and (= :selected mode) configuration-detail)
       [:div
        {:class "text-xs text-base-light-text-paler"}
        (loc/tr :ai-summary-library/owned-by)
        " "
        (owner-label (:owner-id configuration-detail))])
     [save-status save-state]
     [editor-actions state]]))

(defn- detail-panel
  [state]
  [:div
   {:class "min-h-0 overflow-y-auto rounded border border-base-light-border-paler bg-base-light p-4 lg:p-6"}
   (cond
     (= :loading (:detail-state state))
     [:div
      {:class "flex items-center justify-center py-20 text-base-light-text-paler"}
      [:i.fal.fa-spinner.fa-pulse.fa-3x]]

     (= :failed (:detail-state state))
     [:div
      {:class "py-12 text-center text-danger-light-text"}
      (loc/tr :ai-summary-library/detail-load-failed)]

     (and (= :ready (:detail-state state)) (:draft state))
     [configuration-editor-view state]

     :else
     [:div
      {:class "flex flex-col items-center gap-3 py-20 text-center"}
      [:i.fal.fa-books.fa-3x.text-base-light-text-paler]
      [:div
       {:class "text-lg font-semibold text-base-light-text"}
       (loc/tr :ai-summary-library/select-title)]
      [:div
       {:class "max-w-md text-sm text-base-light-text-paler"}
       (loc/tr :ai-summary-library/select-description)]])])

(defn component
  []
  (r/create-class
   {:component-did-mount
    (fn [_]
      (rf/dispatch [::event/initialize]))
    :component-will-unmount
    (fn [_]
      (rf/dispatch [::event/dispose]))
    :reagent-render
    (fn []
      (let [state @(rf/subscribe [::sub/state])]
        [:<>
         [zap-toolbar/view nil breadcrumbs-view nil nil]
         [:div
          {:class "h-[calc(100vh_-_52px-_37px)] overflow-auto bg-base-light text-base-light-text"}
          [:div
           {:class "mx-auto flex w-full max-w-[1600px] flex-col gap-5 px-4 py-6"}
           [:div
            {:class "flex flex-wrap items-start justify-between gap-4"}
            [:div
             [:h1
              {:class "m-0 text-2xl font-bold text-base-light-text"}
              (loc/tr :ai-summary-library/title)]
             [:p
              {:class "mt-1 text-sm text-base-light-text-paler"}
              (loc/tr :ai-summary-library/description)]]
            (when-not (session/is-read-only-user?)
              [button/sm-primary-solid
               {:on-click (fn [_]
                            (rf/dispatch [::event/create-new]))}
               [:i.fal.fa-plus]
               (loc/tr :ai-summary-library/create-action)])]
           [:div
            {:class "grid min-h-[calc(100vh_-_190px)] grid-cols-1 gap-4 lg:grid-cols-[340px_minmax(0,1fr)]"}
            [:div
             {:class "min-h-0 rounded border border-base-light-border-paler bg-base-light p-3"}
             [library-list state]]
            [detail-panel state]]]]]))}))

(defmethod routing/render-view :ai-summary-library
  [_]
  (if (and (not (session/is-guest?))
           (session/has-feature-access? :feature/ai-deal-parsing))
    [default-page-layout/view
     {:class-name ""}
     [component]]
    [not-found/not-found-view]))
